// src/pages/dashboard/customer/Dashboard.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import useEmblaCarousel from 'embla-carousel-react';
import { 
  Search, 
  Users, 
  MessageSquare, 
  Briefcase, 
  Shield, 
  ArrowRight,
  Calendar,
  Banknote,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Card from '../../../components/layout/ui/Card';
import Button from '../../../components/layout/ui/Button';
import RatingDisplay from '../../../components/common/RatingDisplay';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

export default function CustomerDashboard() {
  const { user, api } = useAuth();
  const [currentContracts, setCurrentContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Embla Carousel - Mobile: 1 card, Desktop: 1.85 to show next peek
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 1 },
    }
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  useEffect(() => {
    const fetchCurrentNannies = async () => {
      try {
        const res = await api.get('/contracts/current');
        const contracts = Array.isArray(res.data.data) ? res.data.data : [];
        setCurrentContracts(contracts.filter(c => c.status === 'active'));
      } catch (err) {
        console.error('Failed to load contracts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentNannies();
  }, [api]);

  const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
  const formatDate = (date) => new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${process.env.REACT_APP_IMAGE_URL}${path}`;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 py-8">

      {/* Hero */}
      <div className="text-center py-16 px-8 bg-gradient-to-br from-primary/10 via-secondary/10 to-white rounded-3xl shadow-xl">
        <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
          Welcome back, {user?.first_name || 'Parent'}!
        </h1>
        <p className="text-2xl text-gray-700 max-w-3xl mx-auto">
          Manage your <span className="text-primary font-bold">trusted nannies</span> or find your next perfect match
        </p>

        <div className="flex flex-col sm:flex-row gap-row gap-6 justify-center mt-10">
          <Link to="/dashboard/customer/nannies"
            className="px-12 py-6 text-xl font-bold bg-primary hover:bg-primary/90 text-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all inline-flex items-center justify-center gap-3">
            <Search size={28} />
            Browse Vetted Nannies
          </Link>
          <Link to="/dashboard/customer/offers"
            className="px-12 py-6 text-xl font-bold border-2 border-primary text-primary bg-white hover:bg-primary/5 rounded-2xl shadow-lg transition-all">
            My Offers ({currentContracts.length})
          </Link>
        </div>
      </div>

      {/* Current Nannies - MOBILE PERFECT */}
      {loading ? (
        <div className="flex justify-center py-32"><LoadingSpinner size="lg" /></div>
      ) : currentContracts.length > 0 ? (
        <div className="relative">
          <h2 className="text-4xl font-black text-center mb-10">
            Your Current Nannies ({currentContracts.length})
          </h2>

          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {currentContracts.map((contract) => {
                const nanny = contract.nanny;
                const photoUrl = getPhotoUrl(nanny.profile_picture);

                return (
                  <div key={contract.id} className="flex-none w-full px-4 sm:px-6">
                    <Card className="overflow-hidden shadow-2xl h-full mx-auto max-w-2xl">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-8 sm:p-10">
                        <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                          <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-white/20 backdrop-blur border-4 sm:border-6 border-white/40 overflow-hidden flex-shrink-0">
                            {photoUrl ? (
                              <img src={photoUrl} alt={nanny.full_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-5xl sm:text-6xl font-black">
                                {nanny.full_name[0]}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-3xl sm:text-4xl font-black">{nanny.full_name}</h3>
                            <div className="flex flex-col sm:flex-row items-center gap-4 mt-4">
                              <RatingDisplay rating={nanny.rating || 0} size="lg" />
                              <span className="text-xl opacity-90">Since {formatDate(contract.start_date)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-8 sm:p-10 space-y-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-6 text-center border-2 border-green-200">
                            <Banknote size={40} className="mx-auto text-green-600 mb-3" />
                            <p className="text-3xl font-black text-green-600">{formatMoney(contract.monthly_salary)}</p>
                            <p className="text-lg font-medium text-gray-700">Monthly Salary</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-6 text-center border-2 border-blue-200">
                            <Calendar size={40} className="mx-auto text-primary mb-3" />
                            <p className="text-2xl font-bold text-gray-800">{formatDate(contract.start_date)}</p>
                            <p className="text-lg font-medium text-gray-700">Start Date</p>
                          </div>
                        </div>

                        {contract.terms && (
                          <div>
                            <h4 className="font-bold text-xl text-gray-800 mb-4">Agreed Terms & Benefits:</h4>
                            <div className="bg-blue-50 border-2 border-blue-200 rounded-3xl p-6 text-blue-900 text-base sm:text-lg leading-relaxed">
                              {contract.terms}
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-4 pt-6">
                          <Button to={`/dashboard/messages?with=${nanny.id}`}
                            className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold py-4 text-lg rounded-2xl shadow-lg flex items-center justify-center gap-2">
                            <MessageSquare size={24} />
                            Message {nanny.full_name.split(' ')[0]}
                          </Button>
                          <Button to={`/dashboard/customer/contract/${contract.id}`} variant="outline"
                            className="flex-1 font-bold py-4 text-lg rounded-2xl border-2">
                            View Contract
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          {currentContracts.length > 1 && (
            <>
              <button
                onClick={scrollPrev}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur p-3 sm:p-4 rounded-full shadow-2xl hover:bg-white transition-all z-10"
              >
                <ChevronLeft size={36} className="text-gray-800" />
              </button>
              <button
                onClick={scrollNext}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur p-3 sm:p-4 rounded-full shadow-2xl hover:bg-white transition-all z-10"
              >
                <ChevronRight size={36} className="text-gray-800" />
              </button>
            </>
          )}

          {/* Dots */}
          {currentContracts.length > 1 && (
            <div className="flex justify-center gap-2 mt-10">
              {currentContracts.map((_, i) => (
                <button
                  key={i}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    i === selectedIndex ? 'bg-primary w-10' : 'bg-gray-300 w-2.5'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* No Active Nanny */
        <Card className="text-center py-32 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300 rounded-3xl">
          <Users size={160} className="mx-auto text-gray-300 mb-10" />
          <h3 className="text-5xl font-black text-gray-800 mb-8">No Active Nanny Yet</h3>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
            Start browsing our <strong>fully vetted, verified nannies</strong> and make your first offer today.
          </p>
          <Button to="/dashboard/customer/nannies"
            className="px-24 py-10 text-3xl font-black bg-primary hover:bg-primary/90 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all">
            Find Your Nanny Now
          </Button>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
        <Card className="p-10 text-center bg-gradient-to-br from-purple-50 to-white border-2 border-purple-100 hover:shadow-2xl transition-shadow">
          <Briefcase size={64} className="mx-auto text-secondary mb-6" />
          <p className="text-6xl font-black text-secondary">{currentContracts.length}</p>
          <p className="text-2xl font-medium text-gray-700 mt-4">Active Contracts</p>
        </Card>
        <Card className="p-10 text-center bg-gradient-to-br from-orange-50 to-white border-2 border-orange-100 hover:shadow-2xl transition-shadow">
          <MessageSquare size={64} className="mx-auto text-orange-600 mb-6" />
          <p className="text-6xl font-black text-orange-600">12</p>
          <p className="text-2xl font-medium text-gray-700 mt-4">Unread Messages</p>
        </Card>
        <Card className="p-10 text-center bg-gradient-to-br from-green-50 to-white border-2 border-green-100 hover:shadow-2xl transition-shadow">
          <Shield size={64} className="mx-auto text-green-600 mb-6" />
          <p className="text-6xl font-black text-green-600">127+</p>
          <p className="text-2xl font-medium text-gray-700 mt-4">Vetted Nannies Online</p>
        </Card>
      </div>

      <div className="text-center py-16 bg-gray-50 rounded-3xl border-2 border-gray-200">
        <p className="text-4xl font-bold text-gray-800 mb-6">
          Trusted by <span className="text-primary">1,200+</span> families across Nigeria
        </p>
        <div className="flex flex-wrap justify-center gap-12 mt-10 text-gray-600">
          <div className="flex items-center gap-4"><CheckCircle size={40} className="text-green-600" /><span className="text-2xl font-medium">Background Checked</span></div>
          <div className="flex items-center gap-4"><CheckCircle size={40} className="text-green-600" /><span className="text-2xl font-medium">ID & Guarantor Verified</span></div>
          <div className="flex items-center gap-4"><CheckCircle size={40} className="text-green-600" /><span className="text-2xl font-medium">Reference Checked</span></div>
        </div>
      </div>
    </div>
  );
}